import { useState, useEffect, useCallback } from 'react'
import { XMarkIcon, EyeIcon, EyeSlashIcon, CalendarIcon } from '@heroicons/react/24/outline'
import type { TableColumn } from '@/types/compatibility'
import {
  ValidationSummary
} from '@/components/common/ValidatedForm'
import { toast } from 'react-hot-toast'

interface RowEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: Record<string, unknown>) => Promise<void>
  columns: TableColumn[]
  initialData?: Record<string, unknown>
  isEditing: boolean
  primaryKeys?: string[]
}

export function RowEditModal({
  isOpen,
  onClose,
  onSave,
  columns,
  initialData = {},
  isEditing,
  primaryKeys = []
}: RowEditModalProps) {
  // Helper function to initialize form data
  const createInitialFormData = useCallback(() => {
    const initData: Record<string, unknown> = {}
    
    console.log('🏗️ Creating initial form data:', { columns: columns.length, initialData })
    
    for (const column of columns) {
      if (column.name) {
        // Check if we have initial data for this column
        if (Object.hasOwn(initialData, column.name)) {
          initData[column.name] = initialData[column.name]
        } else {
          // Generate default value based on column type
          const dataType = column.dataType.toLowerCase() || ''
          const { defaultValue: columnDefault } = column
          let defaultValue: unknown = columnDefault
          
          if (!defaultValue) {
            if (dataType.includes('int') || dataType.includes('decimal') || dataType.includes('float') || dataType.includes('double')) {
              defaultValue = 0
            } else if (dataType.includes('bool')) {
              defaultValue = false
            } else if (dataType.includes('date') || dataType.includes('time')) {
              [defaultValue] = new Date().toISOString().split('T')
            } else {
              defaultValue = ''
            }
          }
          
          initData[column.name] = column.nullable ? null : defaultValue
        }
      }
    }
    
    console.log('🏗️ Created form data:', initData)
    return initData
  }, [columns, initialData])

  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [warnings] = useState<Record<string, string>>({})

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      const newFormData = createInitialFormData()
      console.log('� Modal opened - Setting form data:', { isEditing, newFormData })
      setFormData(newFormData)
      setErrors({})
      setShowPasswords({})
    }
  }, [isOpen, isEditing, createInitialFormData]) // Re-run when modal opens or editing mode changes

  // Update form data when initialData changes (for editing different rows)
  useEffect(() => {
    if (isOpen && isEditing && Object.keys(initialData).length > 0) {
      const newFormData = createInitialFormData()
      console.log('🔄 Initial data changed - Updating form data:', { initialData, newFormData })
      setFormData(newFormData)
      setErrors({})
    }
  }, [isOpen, isEditing, initialData, createInitialFormData]) // Re-run when initialData changes

  // Type guard functions
  const isString = (val: unknown): val is string => typeof val === 'string'
  const isNumber = (val: unknown): val is number => typeof val === 'number'
  const isBoolean = (val: unknown): val is boolean => typeof val === 'boolean'
  const isNull = (val: unknown): val is null => val === null
  
  // Convert unknown value to string safely
  const getStringValue = (val: unknown): string => {
    if (isNull(val)) return ''
    if (isString(val)) return val
    if (isNumber(val) || isBoolean(val)) return String(val)
    return ''
  }

  const handleFieldChange = (columnName: string, value: unknown) => {
    console.log('🔄 handleFieldChange called:', { columnName, value, previousValue: formData[columnName] })
    
    // Find the column to get its data type for proper value conversion
    const column = columns.find(col => col.name === columnName)
    const dataType = column?.dataType.toLowerCase() || ''
    
    // Convert value based on data type
    let convertedValue = value
    const stringValue = getStringValue(value)
    
    if (value !== null && stringValue !== '') {
      if (dataType.includes('int')) {
        convertedValue = stringValue === '' ? null : Number.parseInt(stringValue, 10)
        if (typeof convertedValue !== 'number' || Number.isNaN(convertedValue)) convertedValue = value // Keep original if not a valid number
      } else if (dataType.includes('decimal') || dataType.includes('float') || dataType.includes('double')) {
        convertedValue = stringValue === '' ? null : Number.parseFloat(stringValue)
        if (typeof convertedValue !== 'number' || Number.isNaN(convertedValue)) convertedValue = value // Keep original if not a valid number
      } else if (dataType.includes('bool')) {
        convertedValue = Boolean(value)
      }
    }
    
    setFormData(prev => {
      const newData = { ...prev, [columnName]: convertedValue }
      console.log('🔄 Updated formData:', { columnName, from: prev[columnName], to: convertedValue })
      return newData
    })
    
    // Clear error when user starts typing
    if (errors[columnName]) {
      setErrors(prev => ({ ...prev, [columnName]: '' }))
    }
  }

  const validateField = (column: TableColumn, value: unknown): string | null => {
    if (!column.nullable && (value === null || value === '' || value === undefined)) {
      return 'This field is required'
    }

    const dataType = column.dataType.toLowerCase()
    
    if (value !== null && value !== '') {
      // Numeric validation
      if (dataType.includes('int')) {
        const numValue = Number.parseInt(getStringValue(value))
        if (Number.isNaN(numValue)) {
          return 'Must be a valid integer'
        }
      }
      
      if (dataType.includes('decimal') || dataType.includes('float') || dataType.includes('double')) {
        const numValue = Number.parseFloat(getStringValue(value))
        if (Number.isNaN(numValue)) {
          return 'Must be a valid number'
        }
      }

      // Length validation
      if (column.length && typeof value === 'string' && value.length > column.length) {
        return `Maximum length is ${column.length} characters`
      }

      // Email validation for email-like columns
      if (column.name.toLowerCase().includes('email') && typeof value === 'string') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(value)) {
          return 'Must be a valid email address'
        }
      }

      // Enhanced identifier validation for name-like columns
      if ((column.name.toLowerCase().includes('name') || column.name.toLowerCase().includes('username')) && typeof value === 'string') {
        if (value.length < 2) {
          return 'Must be at least 2 characters long'
        }
        if (!/^[a-zA-Z0-9_\s-]+$/.test(value)) {
          return 'Can only contain letters, numbers, spaces, underscores, and hyphens'
        }
      }
    }

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Prepare the final form data with proper type conversion
    const finalFormData: Record<string, unknown> = {}
    
    for (const column of columns) {
      if (column.name && Object.hasOwn(formData, column.name)) {
        const value = formData[column.name]
        
        // Handle null values for nullable fields
        if (value === null && column.nullable) {
          finalFormData[column.name] = null
        } else if (value === '' && column.nullable) {
          finalFormData[column.name] = null
        } else {
          finalFormData[column.name] = value
        }
      }
    }
    
    // For editing, only include changed fields to optimize the payload
    let payloadData = finalFormData
    if (isEditing) {
      const changedData: Record<string, unknown> = {}
      let hasChanges = false
      
      for (const key of Object.keys(finalFormData)) {
        const currentValue = finalFormData[key]
        const originalValue = initialData[key]
        
        // Normalize values for comparison
        const normalizeValue = (val: unknown) => {
          if (val === null || val === undefined || val === '') {
            return null
          }
          return val
        }
        
        const normalizedCurrent = normalizeValue(currentValue)
        const normalizedOriginal = normalizeValue(originalValue)
        
        // Use deep comparison for objects/arrays, simple comparison for primitives
        let hasChanged = false
        hasChanged = typeof normalizedCurrent === 'object' && typeof normalizedOriginal === 'object' ? JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedOriginal) : normalizedCurrent !== normalizedOriginal;
        
        if (hasChanged) {
          changedData[key] = currentValue
          hasChanges = true
          console.log(`🔄 Field '${key}' changed:`, { from: originalValue, to: currentValue })
        }
      }
      
      if (!hasChanges) {
        toast.error('No changes detected')
        return
      }
      
      // Always include primary keys for row identification in updates
      for (const pk of primaryKeys) {
        if (Object.hasOwn(finalFormData, pk)) {
          changedData[pk] = finalFormData[pk]
        }
      }
      
      payloadData = changedData
      console.log('🎯 Detected changes:', changedData)
    }
    
    console.log('📤 Submitting payload:', {
      isEditing,
      originalData: initialData,
      finalFormData,
      payloadData,
      hasChanges: isEditing ? Object.keys(payloadData).length > primaryKeys.length : true
    })
    
    // Validate all fields
    const newErrors: Record<string, string> = {}
    for (const column of columns) {
      if (column.name) {
        const error = validateField(column, finalFormData[column.name])
        if (error) {
          newErrors[column.name] = error
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)
    try {
      await onSave(payloadData)
      onClose()
    } catch (error: unknown) {
      console.error('❌ Save error:', error)
      const msg = error instanceof Error ? error.message : 'Failed to save row'
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderFieldInput = (column: TableColumn) => {
    if (!column.name) return null

    const value = formData[column.name]
    const dataType = column.dataType.toLowerCase() || ''
    const isPrimaryKey = primaryKeys.includes(column.name)
    const isReadOnly = isEditing && isPrimaryKey // Don't allow editing primary keys

    const baseClasses = `w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
      isReadOnly 
        ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' 
        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
    } ${errors[column.name] ? 'border-red-500' : ''}`

    // Boolean fields
    if (dataType.includes('bool')) {
      return (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={isBoolean(value) ? value : false}
            onChange={(e) => { handleFieldChange(column.name, e.target.checked) }}
            disabled={isReadOnly}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {value ? 'True' : 'False'}
          </span>
        </div>
      )
    }

    // Date/Time fields
    if (dataType.includes('date')) {
      return (
        <div className="relative">
          <input
            type="date"
            value={getStringValue(value)}
            onChange={(e) => { handleFieldChange(column.name, e.target.value) }}
            disabled={isReadOnly}
            className={baseClasses}
          />
          <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      )
    }

    if (dataType.includes('timestamp') || dataType.includes('datetime')) {
      // Handle datetime values more robustly
      let dateValue = ''
      const stringValue = getStringValue(value)
      if (stringValue) {
        try {
          const date = new Date(stringValue)
          if (!Number.isNaN(date.getTime())) {
            dateValue = date.toISOString().slice(0, 16)
          }
        } catch {
          console.warn('Invalid date value:', value)
        }
      }
      
      return (
        <input
          type="datetime-local"
          value={dateValue}
          onChange={(e) => { handleFieldChange(column.name, e.target.value) }}
          disabled={isReadOnly}
          className={baseClasses}
        />
      )
    }

    // Password fields (detect by column name)
    if (column.name.toLowerCase().includes('password')) {
      return (
        <div className="relative">
          <input
            type={showPasswords[column.name] ? 'text' : 'password'}
            value={getStringValue(value)}
            onChange={(e) => { handleFieldChange(column.name, e.target.value) }}
            disabled={isReadOnly}
            className={baseClasses}
            placeholder={column.nullable ? 'NULL' : ''}
          />
          <button
            type="button"
            onClick={() => { setShowPasswords(prev => ({ ...prev, [column.name]: !prev[column.name] })) }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPasswords[column.name] ? (
              <EyeSlashIcon className="w-4 h-4" />
            ) : (
              <EyeIcon className="w-4 h-4" />
            )}
          </button>
        </div>
      )
    }

    // Numeric fields
    if (dataType.includes('int') || dataType.includes('decimal') || dataType.includes('float') || dataType.includes('double')) {
      return (
        <input
          type="number"
          value={getStringValue(value)}
          onChange={(e) => {
            const inputValue = e.target.value
            if (inputValue === '') {
              handleFieldChange(column.name, column.nullable ? null : '')
            } else {
              handleFieldChange(column.name, inputValue)
            }
          }}
          disabled={isReadOnly}
          className={baseClasses}
          placeholder={column.nullable ? 'NULL' : '0'}
          step={dataType.includes('int') ? '1' : 'any'}
        />
      )
    }

    // Large text fields
    if (dataType.includes('text') || (column.length && column.length > 255)) {
      return (
        <textarea
          value={getStringValue(value)}
          onChange={(e) => { handleFieldChange(column.name, e.target.value) }}
          disabled={isReadOnly}
          className={`${baseClasses} min-h-[80px] resize-vertical`}
          placeholder={column.nullable ? 'NULL' : ''}
          maxLength={column.length || undefined}
        />
      )
    }

    // Default text input
    return (
      <input
        type="text"
        value={getStringValue(value)}
        onChange={(e) => { handleFieldChange(column.name, e.target.value) }}
        disabled={isReadOnly}
        className={baseClasses}
        placeholder={column.nullable ? 'NULL' : ''}
        maxLength={column.length || undefined}
      />
    )
  }

  if (!isOpen) return null

  // Add debug info at render time
  console.log('🎭 RowEditModal rendering:', {
    isOpen,
    isEditing,
    hasInitialData: Object.keys(initialData).length > 0,
    hasFormData: Object.keys(formData).length > 0,
    initialDataKeys: Object.keys(initialData),
    formDataKeys: Object.keys(formData),
    firstFewInitialData: Object.fromEntries(Object.keys(initialData).slice(0, 3).map(key => [key, initialData[key]])),
    firstFewFormData: Object.fromEntries(Object.keys(formData).slice(0, 3).map(key => [key, formData[key]]))
  })

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Row' : 'Add New Row'}
          </h2>
          <button type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={isSubmitting}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Validation Summary */}
        {Object.keys(errors).length > 0 && (
          <div className="px-6 pt-4">
            <ValidationSummary errors={errors} warnings={warnings} />
          </div>
        )}

        {/* Form */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">          
          <form onSubmit={(e) => void handleSubmit(e)} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {columns.map((column) => {
                if (!column.name) return null
                const isPrimaryKey = primaryKeys.includes(column.name)
                
                return (
                  <div key={column.name} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <div className="flex items-center space-x-2">
                        <span>{column.name}</span>
                        {isPrimaryKey && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">PK</span>
                        )}
                        {!column.nullable && (
                          <span className="text-red-500">*</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {column.dataType}
                        {column.length && ` (${column.length})`}
                        {column.nullable && ' • Nullable'}
                        {column.isAutoIncrement && ' • Auto Increment'}
                      </div>
                    </label>
                    
                    {renderFieldInput(column)}
                    
                    {errors[column.name] && (
                      <p className="text-sm text-red-600">{errors[column.name]}</p>
                    )}

                    {column.nullable && (
                      <button
                        type="button"
                        onClick={() => { handleFieldChange(column.name, null) }}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        Set to NULL
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button type="button"
            onClick={(e) => {
              e.preventDefault()
              const evt: React.FormEvent = e as React.FormEvent
              void handleSubmit(evt)
            }}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 flex items-center"
          >
            {isSubmitting && (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
            )}
            {isEditing ? 'Update Row' : 'Create Row'}
          </button>
        </div>
      </div>
    </div>
  )
}
